import { PencilRuler, FileText, Zap, Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { SectionHeading } from "../components/SectionHeading";

const HERO = "https://customer-assets.emergentagent.com/job_e8a51ae8-0e5c-48cb-98d6-63c31c891959/artifacts/7o2fr7m6_Marketing%20-%20DAO.jpg";

const prestations = [
  {
    icon: PencilRuler,
    title: "Plans & Dessins DAO",
    desc: "Réalisation de plans de maison, de construction, de fabrication et plans électriques en DAO (Dessin Assisté par Ordinateur).",
    image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=900&q=80",
    tone: "from-[#1B3A6B] to-[#0F76B8]",
  },
  {
    icon: FileText,
    title: "Permis de construire & Assainissement",
    desc: "Constitution complète de dossiers de permis de construire (<150 m²) et dossiers pour assainissement non collectif.",
    image: "https://images.unsplash.com/photo-1554995207-c18c203602cb?w=900&q=80",
    tone: "from-[#FBBF24] to-[#F59E0B]",
    iconColor: "text-[#1B3A6B]",
  },
  {
    icon: Zap,
    title: "Plans électriques",
    desc: "Conception détaillée de plans électriques pour habitations et locaux professionnels, conformes aux normes en vigueur.",
    image: "https://images.unsplash.com/photo-1581092335397-9583eb92d232?w=900&q=80",
    tone: "from-[#1B3A6B] to-[#0F76B8]",
  },
  {
    icon: Building2,
    title: "Expertise DAO",
    desc: "Tous travaux en DAO : plans de construction, fabrication, schémas techniques pour vos projets professionnels.",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=900&q=80",
    tone: "from-slate-700 to-slate-900",
    iconColor: "text-[#FBBF24]",
  },
];

export default function DAO() {
  return (
    <div data-testid="page-dao">
      <section className="relative bg-[#0F172A] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${HERO})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/85 to-[#1B3A6B]/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#FBBF24] mb-4">Pôle DAO</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight max-w-3xl">
            Plans & dossiers techniques
            <span className="block text-[#FBBF24]">au bon endroit</span>
          </h1>
          <p className="mt-6 text-lg text-slate-200 max-w-2xl">
            Plans de maison, de construction, de fabrication, électriques, dossier de permis de construire, assainissement…
            Un service professionnel et de proximité pour vos dossiers techniques.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <SectionHeading eyebrow="Nos prestations" title="Études techniques & dessins assistés" subtitle="De l'idée au permis, INGélec rédige et dessine vos dossiers techniques avec précision" />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-7">
          {prestations.map((p) => (
            <article
              key={p.title}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-[0_25px_60px_-20px_rgba(27,58,107,0.35)] transition-all duration-300"
              data-testid={`dao-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="relative h-52 overflow-hidden">
                <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
                <div className={`absolute top-4 left-4 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${p.tone} ${p.iconColor || "text-white"} shadow-xl`}>
                  <p.icon className="w-6 h-6" strokeWidth={2} />
                </div>
              </div>
              <div className="p-7 lg:p-8">
                <h3 className="font-display text-2xl font-bold text-slate-900 tracking-tight">{p.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{p.desc}</p>
                <Link to="/contact" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B3A6B] group-hover:gap-3 transition-all">
                  Demander un devis <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white border border-slate-200 p-10 lg:p-14">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Pourquoi INGélec pour vos plans ?</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
              {[
                "Service professionnel et de proximité",
                "Basé à Sainte-Suzanne, interventions toute l'île",
                "Maîtrise des normes techniques et administratives",
                "Plans électriques cohérents avec les installations terrain",
                "Dossiers de permis (<150 m²) clé en main",
                "Conseil personnalisé selon votre projet",
              ].map((x) => (
                <div key={x} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#FBBF24] mt-0.5 shrink-0" />
                  <span className="text-slate-700">{x}</span>
                </div>
              ))}
            </div>
            <Link to="/contact" className="inline-block mt-10">
              <Button size="lg" className="bg-[#1B3A6B] hover:bg-[#142c54] rounded-full px-8 h-12" data-testid="dao-cta">
                Lancer mon projet DAO <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
